/**
 * The words under the orbs and beside the star — baked once per distinct label and blitted like
 * any other sprite.
 *
 * ## What it is for
 *
 * A player who has never picked one up cannot tell what the glowing thing is. An orb is a gold
 * circle with a core, a shard is a rainbow circle with sparkles and a star is a five-pointed
 * body: three silhouettes that say *something is here* and nothing at all about what. So every
 * orb carries its own name and the star carries the word `Star`.
 *
 * **IT GIVES AWAY NOTHING, WHICH IS THE ONLY REASON IT IS ALLOWED.** Orbs and stars replicate
 * **room-wide** rather than by interest — `powerUps` and `stars` are the room's whole set on
 * every `powerup` tick — so a label names a thing this client was already sent, at a place it was
 * already drawing. It is presentation over data already in hand, exactly as the bot badge and the
 * country chip are: no wire change, no simulation change, nothing hashed.
 *
 * ## Why a texture and not a `Text` object
 *
 * `flag-chips.ts`' bargain, one entity along and for its reason: a `Phaser.Text` is a canvas, a
 * texture upload and a display-list entry, and one rebuilt inside `draw` is exactly the churn the
 * render-cost card was written about. There are at most `MAX_POWER_UPS_PER_ROOM` orbs and one
 * star in a room, and several orbs are routinely the same kind — so the cache is keyed on the
 * **label**, not on the entity, and a room whose four orbs are all rockets holds one texture.
 *
 * ## Why the name comes out of the registry
 *
 * `POWER_UPS` already carries a `name` and the inventory strip already draws it, so this reads
 * the same field through the same lookup: a tenth power-up labels itself with no edit here. An id
 * this build does not know reads as the generic noun rather than as a broken label, which is
 * `NoticeOverlay`'s rule — a client one build behind a room should still read something that
 * makes sense.
 *
 * ## Why the two inks are fixed rather than the palette's
 *
 * Near-white glyphs inside a dark outline, which are `flag-chips.ts`' own two inks and are
 * theme-independent on purpose: a word over the arena is drawn over a hue the wire chose, a
 * photograph somebody uploaded or bare ground, and a stroked glyph is legible against all three
 * on both grounds. It is also what keeps a theme change free — `ArenaScene.applyPalette`
 * re-states the colours a frame does not redraw for itself, and a label that took a palette
 * colour would mean re-baking every texture in the cache on the switch.
 *
 * ## What is here and what is in the scene
 *
 * Everything worth asserting: the word, how big it is on screen, when it stops being drawn and
 * where it sits relative to the body it names. The engine is reached through `FieldLabelHost`,
 * four methods wide, so all of it is ordinary logic a test can drive without a canvas —
 * `flag-chips.ts`' arrangement exactly.
 */

import { getPowerUp } from '@chompdome/shared/powerups';

/**
 * The em size a label is drawn at on screen, in **CSS** pixels — a screen constant, never a
 * world one.
 *
 * **A LABEL THAT SCALED WITH THE WORLD WOULD BE THE BUG.** The camera walks the whole
 * `VIEW_BANDS` ladder, from a 320-unit half-height to 1 150, so a word sized in world units is
 * either a smear at one end of that ladder or most of the arena at the other. A word is read at a
 * size the eye measures, so it is sized in the units the eye measures in.
 *
 * CSS pixels rather than the drawing buffer's, which is `grid.ts`' distinction and for its
 * reason: `camera.zoom` maps world units to the *drawing buffer* and the buffer is up to
 * `MAX_RENDER_SCALE` times the element, so the product with `ScaleManager.zoom` is what a person
 * actually sees. (The player name labels take the buffer measure. They are a different feature
 * and are not changed here.)
 */
export const FIELD_LABEL_TEXT_PX = 13;

/**
 * The label box's height as a multiple of the em: room for a descender and for the outline.
 *
 * One number for both the texture and the world size, so the picture that is baked and the box it
 * is drawn into can never be different shapes.
 */
export const FIELD_LABEL_BOX_SCALE = 1.5;

/**
 * How wide a body has to be drawn, in CSS pixels, before it is worth naming — and the ramp above
 * it.
 *
 * **THIS IS THE HALF THAT KEEPS A LABEL FROM BEING WORSE THAN NOTHING.** The label holds a screen
 * size, so as the camera pulls back the body shrinks under it and the word does not: past some
 * band the caption is bigger than the thing it captions, which reads as a word floating in an
 * empty arena. It is dropped there rather than drawn smaller, because a word drawn smaller is a
 * word nobody can read.
 *
 * **BOTH NUMBERS ARE THE EM ITSELF, WHICH IS WHAT MAKES THIS A DERIVED RULE RATHER THAN A TASTE.**
 * The label starts appearing where the body is as wide as the word is tall and is full where it is
 * twice that — so a caption whose letters are taller than the thing they caption never exists, and
 * moving `FIELD_LABEL_TEXT_PX` moves the drop with it rather than leaving two numbers to disagree.
 *
 * It is measured against the **body**, so a star (which is bigger) keeps its label a band or two
 * longer than an orb does. That falls out of the rule rather than being chosen, which is the point
 * of writing the rule this way round.
 *
 * The fade is what stops it being a pop: the zoom tween crosses a band over `ZOOM_TWEEN_MS`, and a
 * label that blinked out mid-tween would read as a glitch.
 *
 * Where that lands, measured on the two surfaces that matter — the reference landscape phone
 * (844 x 390, so 195 CSS pixels of half-height) and a 987-pixel desktop window:
 *
 *   | band half-height | phone orb | phone star | desktop orb | desktop star |
 *   |              320 |      1.00 |       1.00 |        1.00 |         1.00 |
 *   |              485 |      0.36 |       0.73 |        1.00 |         1.00 |
 *   |              640 |      0.03 |       0.31 |        1.00 |         1.00 |
 *   |            1 150 |         0 |          0 |        0.45 |         0.85 |
 *
 * A phone keeps the label through the bands a small player is actually framed at — which is the
 * whole audience for it, since somebody who has grown past those has met an orb — and gives it up
 * as the arena pulls back. A desktop keeps it nearly everywhere, which is what its pixels buy.
 */
export const FIELD_LABEL_MIN_BODY_PX = FIELD_LABEL_TEXT_PX;
export const FIELD_LABEL_FADE_BODY_PX = FIELD_LABEL_TEXT_PX;

/**
 * The clearance between the body's halo and the label box, as a fraction of the box's height.
 *
 * Proportional to the label rather than to the body, so it is a screen constant like everything
 * else about this cue: the gap looks the same at every band.
 */
export const FIELD_LABEL_GAP_SCALE = 0.35;

/** The size the glyphs are baked at. Well above what they are drawn at — see the texture size. */
export const FIELD_LABEL_FONT_PX = 48;

/** Side padding inside the texture, so the outline is never clipped by the texture's own edge. */
export const FIELD_LABEL_PAD_PX = 10;

/**
 * The widest texture one label may take.
 *
 * Nothing in `POWER_UPS` comes close — the longest name today is `Invisibility` — so this is a
 * ceiling against an id from a build that does not exist yet, not a number the game reaches.
 * Past it the glyphs are condensed into the box, which is `paintFlagChip`'s `maxWidth` trick.
 */
export const FIELD_LABEL_MAX_TEXTURE_PX = 512;

/**
 * Distinct labels one client holds at a time.
 *
 * `POWER_UPS` is nine entries, plus the star's word and the generic noun: this is a ceiling a
 * real match cannot reach, here so that a room minting unknown kinds cannot turn a caption into
 * unbounded texture memory. Past it `get` answers undefined and the fixture is drawn unlabelled,
 * which is exactly what it looked like before this card.
 */
export const MAX_FIELD_LABELS = 16;

/** The star's word. One kind of thing, so one word — there is nothing to look up. */
export const STAR_FIELD_LABEL = 'Star';

/**
 * What an orb of a kind this build has never heard of is called.
 *
 * The same noun `inventory-strip.ts`' `UNKNOWN_SLOT_NAME` uses, restated rather than imported
 * because `hud/` reads `scenes/` and never the other way round (`minimap.ts` imports
 * `./scenes/star`). `field-labels.test.ts` asserts the two agree, so they cannot drift while the
 * direction stays one-way.
 */
export const UNKNOWN_FIELD_LABEL = 'Power-up';

const FIELD_LABEL_FILL = '#f8fafc';
const FIELD_LABEL_EDGE = '#0f172a';

/** Where a label sits relative to the body it names. */
export type FieldLabelPlacement = 'below' | 'beside';

/** A point relative to the body's centre, in world units. */
export interface FieldLabelOffset {
  x: number;
  y: number;
}

/** The box a label is drawn into, in world units. */
export interface FieldLabelBox {
  width: number;
  height: number;
}

/** A baked label: the texture to blit and how much wider than it is tall. */
export interface FieldLabel {
  key: string;
  /** Texture width over texture height, so a caller can turn one world height into a box. */
  aspect: number;
}

/** The texture key one label is drawn under. Prefixed so it cannot collide with a scene asset. */
export function fieldLabelKey(text: string): string {
  return `field-label:${text}`;
}

/** The font one label is painted with, at a given size. The name labels' family. */
export function fieldLabelFont(sizePx: number): string {
  return `600 ${sizePx}px system-ui, sans-serif`;
}

/**
 * What an orb of this kind is called: the registry's own `name`, or the generic noun for an id
 * this build does not know.
 *
 * A lookup and never a branch, which is the rule every reader of `POWER_UPS` is under — a tenth
 * power-up names itself here with no edit at all.
 */
export function fieldLabelName(kind: string): string {
  return getPowerUp(kind)?.name ?? UNKNOWN_FIELD_LABEL;
}

/**
 * How opaque a label is for a body drawn `bodyPx` across, 0..1.
 *
 * Zero below `FIELD_LABEL_MIN_BODY_PX` — where the word would be bigger than the thing it names —
 * and full by `FIELD_LABEL_FADE_BODY_PX` above it, so crossing a band fades the caption instead of
 * blinking it.
 */
export function fieldLabelAlpha(bodyPx: number): number {
  if (!Number.isFinite(bodyPx) || bodyPx <= FIELD_LABEL_MIN_BODY_PX) return 0;
  const t = (bodyPx - FIELD_LABEL_MIN_BODY_PX) / FIELD_LABEL_FADE_BODY_PX;
  return t >= 1 ? 1 : t;
}

/**
 * The label box in world units, for a texture of this aspect at this many CSS pixels per world
 * unit.
 *
 * The height is the screen constant divided by the zoom, which is the whole of "it holds a screen
 * size"; the width comes off the texture's own aspect so a long word is a long box and nothing
 * about the picture is stretched. A zoom of zero — a camera that has not been sized yet — answers
 * a zero box rather than an infinity, and `fieldLabelAlpha` has already hidden it by then anyway.
 */
export function fieldLabelBox(aspect: number, cssZoom: number): FieldLabelBox {
  if (!(cssZoom > 0) || !Number.isFinite(aspect)) return { width: 0, height: 0 };
  const height = (FIELD_LABEL_TEXT_PX * FIELD_LABEL_BOX_SCALE) / cssZoom;
  return { width: height * aspect, height };
}

/**
 * Where the label's centre goes, relative to the body's, in world units.
 *
 * **IT MUST NEVER COVER THE BODY IT NAMES**, which is the one hard rule here: an orb's core and a
 * star's points are how they are told apart at distance, and a caption over either costs more
 * than it buys. So both placements clear `halo` — the outer edge of the glow, which is the widest
 * the fixture is ever drawn — by a gap of its own before the box even starts.
 *
 * `below` for an orb: a blob arriving from above is what a player is watching for, so the space
 * over a fixture is the space worth leaving clear. `beside` for the star, which is what was asked
 * for and what the star's size makes room for.
 */
export function fieldLabelOffset(
  placement: FieldLabelPlacement,
  halo: number,
  box: FieldLabelBox,
): FieldLabelOffset {
  const gap = box.height * FIELD_LABEL_GAP_SCALE;
  return placement === 'below'
    ? { x: 0, y: halo + gap + box.height / 2 }
    : { x: halo + gap + box.width / 2, y: 0 };
}

/** The texture a label of this ink width is baked into. Square-ish is not wanted; wide is. */
export function fieldLabelTextureSize(inkPx: number): FieldLabelBox {
  const width = Math.min(
    Math.max(Math.ceil(inkPx) + FIELD_LABEL_PAD_PX * 2, FIELD_LABEL_FONT_PX),
    FIELD_LABEL_MAX_TEXTURE_PX,
  );
  return { width, height: Math.round(FIELD_LABEL_FONT_PX * FIELD_LABEL_BOX_SCALE) };
}

/**
 * Paint one label into a 2D context. Exported for the test, which is the only way to assert the
 * order without a GPU: the outline has to be **stroked before** the glyphs are filled, or half of
 * every stroke is painted over the letterform it is supposed to be carrying.
 */
export function paintFieldLabel(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  text: string,
): void {
  ctx.clearRect(0, 0, width, height);
  ctx.font = fieldLabelFont(FIELD_LABEL_FONT_PX);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.lineWidth = Math.max(FIELD_LABEL_FONT_PX * 0.18, 2);
  ctx.strokeStyle = FIELD_LABEL_EDGE;
  const room = width - FIELD_LABEL_PAD_PX * 2;
  ctx.strokeText(text, width / 2, height / 2, room);
  ctx.fillStyle = FIELD_LABEL_FILL;
  ctx.fillText(text, width / 2, height / 2, room);
}

/**
 * The slice of the engine this needs. Implemented over Phaser's texture manager and a scratch
 * canvas by `ArenaScene`, and by a plain object in the tests.
 */
export interface FieldLabelHost {
  /**
   * How wide `text` is when painted with `font`, in pixels — or null when no measuring context is
   * available, which is the caller's cue to draw the fixture unlabelled.
   */
  measure(text: string, font: string): number | null;
  /**
   * Create a `width` x `height` canvas texture under `key` and hand back its 2D context, or null
   * if the engine would not give one (a lost context, a key already taken).
   */
  create(key: string, width: number, height: number): CanvasRenderingContext2D | null;
  /** The canvas has been painted; upload it. */
  refresh(key: string): void;
  /** Release a texture and the memory behind it. */
  remove(key: string): void;
}

export class FieldLabels {
  /** Labels that have a texture, and how wide each one came out. */
  private readonly baked = new Map<string, FieldLabel>();
  /** Labels the host refused, so a broken canvas costs one attempt rather than one a frame. */
  private readonly refused = new Set<string>();

  constructor(private readonly host: FieldLabelHost) {}

  /**
   * The texture for this word, or undefined for "draw no label".
   *
   * Called once per fixture per frame, so the hit path is a single `Map` lookup — which is the
   * whole reason the cache is keyed on the *label* rather than on the entity: four rocket orbs
   * are four hits on one entry.
   */
  get(text: string): FieldLabel | undefined {
    const key = fieldLabelKey(text);
    const baked = this.baked.get(key);
    if (baked !== undefined) return baked;
    if (this.refused.has(key) || this.baked.size >= MAX_FIELD_LABELS) return undefined;

    const ink = this.host.measure(text, fieldLabelFont(FIELD_LABEL_FONT_PX));
    if (ink === null || !Number.isFinite(ink)) {
      this.refused.add(key);
      return undefined;
    }

    const size = fieldLabelTextureSize(ink);
    const ctx = this.host.create(key, size.width, size.height);
    if (ctx === null) {
      this.refused.add(key);
      return undefined;
    }

    paintFieldLabel(ctx, size.width, size.height, text);
    this.host.refresh(key);
    const label: FieldLabel = { key, aspect: size.width / size.height };
    this.baked.set(key, label);
    return label;
  }

  /** Give up every texture. Called when the scene goes away, so a leaving match frees its GPU. */
  destroy(): void {
    for (const key of this.baked.keys()) this.host.remove(key);
    this.baked.clear();
    this.refused.clear();
  }
}
